import { useState, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import propertiesServices from '../services/propertiesServices';
import proposalsServices from '../services/proposalsServices';
import warrantyTicketServices from '../services/warrantyTicketServices';
import suppliersServices from '../services/suppliersServices';
import { stringifyAddress } from '../utils/formatter';
import { getOrderTypeById } from '../components/WarrantyTicketOrderTypeSelector';

export const PRIORITIES = ['High', 'Medium', 'Low'];

export const WORK_STATUSES = [
  { value: 'Completed on-site', label: 'Completed on-site' },
  { value: 'Parts needed', label: 'Parts needed — must order' },
  { value: 'Parts ordered', label: 'Parts ordered' },
  { value: 'Parts received', label: 'Parts received' },
  { value: 'Follow-up scheduled', label: 'Follow-up visit scheduled' },
  { value: 'Follow-up needed', label: 'Follow-up needed' },
];

export const STEP_LABELS = ['Scenario', 'Customer & PO', 'Issue', 'Parts / Follow-up'];

const ALLOWED_PROPOSAL_STATUSES = ['Completed', 'Pending'];
const PARTS_STATUSES = ['Parts needed', 'Parts ordered', 'Parts received'];

// Force upper-case for identifier-like part fields (model/part numbers, PO,
// manual supplier name). Special Instructions is free prose, so it is excluded.
const UPPERCASE_PART_FIELDS = ['partDescription', 'poNumber', 'orderedFrom', 'failedPartNumber', 'failedPartSerial'];

const emptyForm = () => ({
  issueDescription: '', priority: 'Medium', workStatus: '',
  installationYear: null, brandModel: '', defectiveModel: '', defectiveSerial: '',
  customerPhone: '',
  followUpDate: '', followUpTime: '', followUpReason: '', partsLocation: '',
});

const emptyPart = () => ({
  partDescription: '', orderedFrom: '', orderDate: '', expectedArrival: '',
  poNumber: '', qty: 1, instruction: '', supplierId: null,
  // itemType drives the failed-part autofill: 'part' mirrors the description,
  // 'equipment' copies the ticket's defective model/serial.
  itemType: 'part', failedPartNumber: '', failedPartSerial: 'N/A',
});

/**
 * All non-presentational logic shared by the two "create warranty ticket"
 * surfaces (the standalone page and the in-property dialog). Each surface
 * renders its own chrome + address section but drives everything through the
 * controller object returned here.
 *
 * @param {object}   opts
 * @param {object}   opts.userData    current user (for email signature / role)
 * @param {function} opts.onSuccess   called with the created ticket on success
 */
export default function useWarrantyTicketForm({ userData, onSuccess }) {
  const userRole = userData?.role || '';
  const isAdminLike = userRole === 'superadmin' || userRole === 'admin';

  const [expertMode, setExpertMode] = useState(isAdminLike);
  const [currentStep, setCurrentStep] = useState(0);

  const [showReview, setShowReview] = useState(false);
  const [emailTexts, setEmailTexts] = useState({});

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [legacyProposals, setLegacyProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [selectedLegacy, setSelectedLegacy] = useState(null);
  const [notAirmaxxInstallation, setNotAirmaxxInstallation] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);

  const [form, setForm] = useState(emptyForm());
  const [orderType, setOrderType] = useState(null);
  const [parts, setParts] = useState([emptyPart()]);
  const [suppliers, setSuppliers] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [softWarnings, setSoftWarnings] = useState([]);
  const [softWarningsOpen, setSoftWarningsOpen] = useState(false);
  const [ccProjectUrl, setCcProjectUrl] = useState(null);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);

  useEffect(() => {
    setExpertMode(isAdminLike);
  }, [isAdminLike]);

  const loadSuppliers = useCallback(() => {
    suppliersServices.getSuppliers().then(res => {
      if (res.status === 200) setSuppliers(res.data.value?.data || res.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  // ---- property / proposals ----

  const fetchCcUrl = useCallback((propId) => {
    setCcProjectUrl(null);
    propertiesServices
      .listCcProjects(propId)
      .then((res) => {
        const url = Array.isArray(res.data) && res.data[0]?.ccProjectUrl;
        setCcProjectUrl(url || null);
      })
      .catch(() => setCcProjectUrl(null));
  }, []);

  const applyPropertyDetails = useCallback((fullProperty) => {
    const propClients = fullProperty.clients || [];
    if (propClients.length > 0) {
      const c = propClients[0]?.client || propClients[0];
      setClientInfo(c);
      setForm(prev => ({ ...prev, customerPhone: c.phoneNumber || c.mobile || '' }));
    }
    setProposals((fullProperty.proposals || []).filter(p => ALLOWED_PROPOSAL_STATUSES.includes(p.status)));
    setLegacyProposals((fullProperty.legacyProposals || []).filter(lp => ALLOWED_PROPOSAL_STATUSES.includes(lp.status)));
  }, []);

  const clearProperty = useCallback(() => {
    setSelectedProperty(null);
    setProposals([]);
    setLegacyProposals([]);
    setSelectedProposal(null);
    setSelectedLegacy(null);
    setNotAirmaxxInstallation(false);
    setClientInfo(null);
    setCcProjectUrl(null);
    setSelectedPhotoIds([]);
  }, []);

  // Used by the standalone page: the address comes from a search result that
  // already carries the address fields, then we fetch the rest of the details.
  const selectProperty = useCallback(async (property) => {
    if (!property) { clearProperty(); return; }
    setNotAirmaxxInstallation(false);
    setSelectedProperty(property);
    setSelectedProposal(null);
    setSelectedLegacy(null);
    setSelectedPhotoIds([]);
    fetchCcUrl(property.id);
    try {
      const res = await propertiesServices.getPropertyById(property.id);
      if (res.status === 200) {
        // Replace the lean search result with the full property so dependent
        // UI (e.g. the Installed Equipment picker) sees equipment & co.
        setSelectedProperty(res.data);
        applyPropertyDetails(res.data);
      }
    } catch (e) {
      console.error('Error fetching property details:', e);
    }
  }, [clearProperty, fetchCcUrl, applyPropertyDetails]);

  // Used by the dialog: we only have the id, so fetch the full property.
  const loadPropertyById = useCallback(async (propId) => {
    setPropertyLoading(true);
    try {
      const res = await propertiesServices.getPropertyById(propId);
      if (res.status === 200) {
        setSelectedProperty(res.data);
        applyPropertyDetails(res.data);
      }
    } catch (e) {
      console.error('Error fetching property details:', e);
    }
    fetchCcUrl(propId);
    setPropertyLoading(false);
  }, [applyPropertyDetails, fetchCcUrl]);

  // Full reset of the editable form state (dialog re-open).
  const resetForm = useCallback(() => {
    setSelectedProposal(null);
    setSelectedLegacy(null);
    setNotAirmaxxInstallation(false);
    setClientInfo(null);
    setForm(emptyForm());
    setParts([emptyPart()]);
    setExpandedRows({});
    setErrors({});
    setShowReview(false);
    setEmailTexts({});
    setOrderType(null);
    setCcProjectUrl(null);
    setSelectedPhotoIds([]);
    setCurrentStep(0);
    setExpertMode(isAdminLike);
  }, [isAdminLike]);

  const handleProposalSelect = async (value) => {
    if (!value) {
      setSelectedProposal(null);
      setSelectedLegacy(null);
      setForm(prev => ({ ...prev, brandModel: '' }));
      return;
    }
    if (value.startsWith('legacy:')) {
      const legacyNum = value.replace('legacy:', '');
      setSelectedLegacy(legacyProposals.find(l => l.proposalNumber === legacyNum));
      setSelectedProposal(null);
      setForm(prev => ({ ...prev, brandModel: '' }));
      return;
    }
    setSelectedLegacy(null);
    try {
      const res = await proposalsServices.getProposalById(value);
      if (res.status === 200) {
        const proposal = res.data;
        setSelectedProposal(proposal);
        let brandModel = '';
        if (proposal.proposalSystems?.length > 0) {
          const sys = proposal.proposalSystems[0];
          if (sys.equipment?.length > 0) {
            const eq = sys.equipment[0];
            brandModel = `${eq.make?.name || ''} ${eq.model?.name || ''}`.trim().toUpperCase();
          }
        }
        setForm(prev => ({ ...prev, brandModel }));
      }
    } catch (e) {
      console.error('Error fetching proposal:', e);
    }
  };

  const handleNotAirmaxxToggle = (checked) => {
    setNotAirmaxxInstallation(checked);
    if (checked) {
      setSelectedProposal(null);
      setSelectedLegacy(null);
      setForm(prev => ({ ...prev, brandModel: '' }));
    }
  };

  // ---- form / parts ----

  const handleOrderTypeChange = (id) => {
    setOrderType(id);
    const def = getOrderTypeById(id);
    if (def?.workStatus) {
      setForm((prev) => {
        const FOLLOWUP = ['Follow-up scheduled', 'Follow-up needed'];
        const family = (ws) => {
          if (!ws) return null;
          if (PARTS_STATUSES.includes(ws)) return 'parts';
          if (FOLLOWUP.includes(ws)) return 'follow-up';
          if (ws === 'Completed on-site') return 'on-site';
          return null;
        };
        if (family(prev.workStatus) && family(prev.workStatus) === family(def.workStatus)) return prev;
        return { ...prev, workStatus: def.workStatus };
      });
    }
  };

  const handleFormChange = (field, value) => {
    // Keep equipment-mode failed-part serials in sync with the defective serial.
    if (field === 'defectiveSerial') {
      const oldSerial = (form.defectiveSerial || '').trim();
      const next = (value || '').toUpperCase();
      setParts(prev => prev.map(p => {
        if ((p.itemType || 'part') !== 'equipment') return p;
        const cur = (p.failedPartSerial || '').trim();
        if (cur === '' || cur === 'N/A' || cur === oldSerial) return { ...p, failedPartSerial: next || 'N/A' };
        return p;
      }));
      value = next;
    }
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  // Defective model is entered upper-case and auto-copied into the first part
  // of the order form (unless the tech already typed something else there).
  const handleDefectiveModelChange = (rawValue) => {
    const value = (rawValue || '').toUpperCase();
    const oldModel = (form.defectiveModel || '').trim();
    setParts(prev => prev.map((p, i) => {
      let next = p;
      const failed = (p.failedPartNumber || '').trim();
      const failedFollows = failed === '' || failed === oldModel;
      if (i === 0) {
        const desc = (p.partDescription || '').trim();
        if (desc === '' || desc === oldModel) {
          next = { ...next, partDescription: value };
          // part-mode failed part # mirrors the description
          if ((p.itemType || 'part') === 'part' && failedFollows) next = { ...next, failedPartNumber: value };
        }
      }
      if ((p.itemType || 'part') === 'equipment' && failedFollows) {
        next = { ...next, failedPartNumber: value };
      }
      return next;
    }));
    handleFormChange('defectiveModel', value);
  };

  const handlePartChange = (index, field, value) => {
    const v = (UPPERCASE_PART_FIELDS.includes(field) && typeof value === 'string')
      ? value.toUpperCase()
      : value;
    setParts(prev => prev.map((p, i) => {
      if (i !== index) return p;
      // In "part" mode the failed part # follows the description while the
      // tech hasn't typed a custom value into it.
      if (field === 'partDescription' && (p.itemType || 'part') === 'part') {
        const failed = (p.failedPartNumber || '').trim();
        const oldDesc = (p.partDescription || '').trim();
        if (failed === '' || failed === oldDesc) {
          return { ...p, partDescription: v, failedPartNumber: v };
        }
      }
      return { ...p, [field]: v };
    }));
  };

  // Part / Equipment toggle: prefill the failed-part fields accordingly.
  const handlePartItemTypeChange = (index, itemType) => {
    setParts(prev => prev.map((p, i) => {
      if (i !== index) return p;
      if (itemType === 'equipment') {
        return {
          ...p,
          itemType,
          failedPartNumber: (form.defectiveModel || '').toUpperCase(),
          failedPartSerial: (form.defectiveSerial || '').toUpperCase() || 'N/A',
        };
      }
      return {
        ...p,
        itemType,
        failedPartNumber: p.partDescription || '',
        failedPartSerial: 'N/A',
      };
    }));
  };

  const handlePartSupplierChange = (index, supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setParts(prev => prev.map((p, i) => i === index ? {
      ...p, supplierId,
      orderedFrom: supplierId === 'phone' ? 'Ordered by Phone' : supplierId === 'other' ? '' : (supplier?.name || ''),
    } : p));
  };

  const toggleRowExpanded = (index) => setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
  const addPart = () => setParts(prev => [...prev, emptyPart()]);
  const removePart = (index) => {
    setParts(prev => prev.filter((_, i) => i !== index));
    setExpandedRows(prev => {
      const next = {};
      Object.keys(prev).forEach(k => {
        const ki = parseInt(k);
        if (ki < index) next[ki] = prev[ki];
        else if (ki > index) next[ki - 1] = prev[ki];
      });
      return next;
    });
  };

  // ---- derived ----

  const showSystemInfo = (selectedProposal || selectedLegacy) || notAirmaxxInstallation;

  const selectedSuppliers = useMemo(() => {
    const ids = new Set();
    const result = [];
    parts.forEach(p => {
      if (p.supplierId && !ids.has(p.supplierId)) {
        ids.add(p.supplierId);
        if (p.supplierId === 'phone') result.push({ id: 'phone', name: 'Ordered by Phone', emails: [] });
        else if (p.supplierId === 'other') result.push({ id: 'other', name: p.orderedFrom || 'Other', emails: [] });
        else {
          const s = suppliers.find(sup => sup.id === p.supplierId);
          if (s) result.push(s);
        }
      }
    });
    return result;
  }, [parts, suppliers]);

  const partsHaveSuppliers = parts.some(p => p.supplierId && p.partDescription.trim());
  const proposalSelectValue = selectedProposal?.id || (selectedLegacy ? `legacy:${selectedLegacy.proposalNumber}` : '');

  // ---- email ----

  const generateEmailText = (supplierId, ticketNum) => {
    const supplierParts = parts.filter(p => p.supplierId === supplierId && p.partDescription.trim());
    const supplier = suppliers.find(s => s.id === supplierId);
    const supplierName = supplierId === 'other' ? 'Supplier' : (supplier?.name || 'Supplier');
    const address = selectedProperty ? stringifyAddress(selectedProperty) : '';
    const partsList = supplierParts.map(p => `${p.partDescription}${p.qty > 1 ? ` (x${p.qty})` : ''}`).join('\n');
    const instructions = supplierParts.filter(p => p.instruction).map(p => `Note for "${p.partDescription}": ${p.instruction}`).join('\n');
    const techName = userData?.fullName || '';
    const techEmail = userData?.email || '';
    const techPhone = userData?.phoneNumber || '';
    let contactLine = '';
    if (techEmail && techPhone) contactLine = `\nYou can contact me by email: ${techEmail} and cell phone: ${techPhone}`;
    else if (techEmail) contactLine = `\nYou can contact me by email: ${techEmail}`;
    else if (techPhone) contactLine = `\nYou can contact me by cell phone: ${techPhone}`;

    const defModel = form.defectiveModel ? `Model: ${form.defectiveModel}` : '';
    const defSerial = form.defectiveSerial ? `Serial #: ${form.defectiveSerial}` : '';
    const defectiveLine = [defModel, defSerial].filter(Boolean).join('\n');

    return `*** PLEASE DO NOT REPLY TO THIS EMAIL — this mailbox is not monitored. ***
*** For any questions regarding this order, please use the contact information below. ***

Dear ${supplierName}

Please process this equipment order:

${address ? `Property: ${address}\n` : ''}PO: ${ticketNum || ''}
${defectiveLine ? `\n${defectiveLine}\n` : ''}
Parts needed:
${partsList}
${instructions ? `\n${instructions}\n` : ''}${contactLine}

Thank you${techName ? `\n${techName}` : ''}`;
  };

  const handleReviewOrder = () => {
    const texts = {};
    selectedSuppliers.forEach(s => { texts[s.id] = generateEmailText(s.id, '(will be assigned)'); });
    setEmailTexts(texts);
    setShowReview(true);
  };

  // ---- validation / submit ----

  const validate = () => {
    const newErrors = {};
    if (!selectedProperty) newErrors.property = 'Select an address';
    if (selectedProperty && !notAirmaxxInstallation && !selectedProposal && !selectedLegacy) {
      newErrors.proposal = 'Select a proposal or mark "Not Airmaxx Installation"';
    }
    if (!form.issueDescription || !form.issueDescription.trim()) newErrors.issueDescription = 'Issue description is required';
    if (form.issueDescription.length > 2000) newErrors.issueDescription = 'Max 2000 characters';
    if (!form.workStatus) newErrors.workStatus = 'Select work status';
    if (PARTS_STATUSES.includes(form.workStatus)) {
      parts.forEach((part, i) => { if (!part.partDescription) newErrors[`part_${i}_desc`] = 'Part description required'; });
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) toast.error(Object.values(newErrors)[0]);
    return newErrors;
  };

  const stepForErrors = (errs) => {
    if (errs.property || errs.proposal) return 1;
    if (errs.issueDescription || errs.workStatus) return 2;
    if (Object.keys(errs).some((k) => k.startsWith('part_'))) return 3;
    return null;
  };

  const computeSoftWarnings = () => {
    const warnings = [];
    if (orderType === 'large') {
      if (!form.defectiveModel?.trim() || !form.defectiveSerial?.trim()) {
        warnings.push('Large unit: defective model and serial number are missing. Suppliers need them for the warranty claim.');
      }
      const phoneParts = parts.filter((p) => p.supplierId === 'phone' && p.partDescription?.trim());
      if (phoneParts.length > 0) {
        warnings.push(`Large unit: ${phoneParts.length} part(s) marked as "Ordered by Phone". The supplier will not get an email and the warehouse will not register the incoming unit.`);
      }
      const noSupplierParts = parts.filter((p) => !p.supplierId && p.partDescription?.trim());
      if (noSupplierParts.length > 0) {
        warnings.push(`Large unit: ${noSupplierParts.length} part(s) have no supplier selected — order will not be sent.`);
      }
    }
    return warnings;
  };

  const handleSubmit = async () => {
    const verrors = validate();
    if (Object.keys(verrors).length > 0) {
      if (!expertMode) {
        const s = stepForErrors(verrors);
        if (s !== null) setCurrentStep(s);
      }
      return;
    }
    const w = computeSoftWarnings();
    if (w.length > 0 && !softWarningsOpen) {
      setSoftWarnings(w);
      setSoftWarningsOpen(true);
      return;
    }
    setSoftWarningsOpen(false);
    setSubmitting(true);
    try {
      const data = {
        propertyId: selectedProperty.id,
        proposalId: notAirmaxxInstallation ? undefined : (selectedProposal?.id || undefined),
        legacyProposalNumber: notAirmaxxInstallation ? undefined : (selectedLegacy?.proposalNumber || undefined),
        notAirmaxxInstallation: notAirmaxxInstallation || undefined,
        clientId: clientInfo?.id || undefined,
        issueDescription: form.issueDescription,
        priority: form.priority,
        workStatus: form.workStatus,
        installationYear: form.installationYear || undefined,
        brandModel: form.brandModel || undefined,
        defectiveModel: form.defectiveModel || undefined,
        defectiveSerial: form.defectiveSerial || undefined,
        customerPhone: form.customerPhone || undefined,
        followUpDate: form.followUpDate || undefined,
        followUpTime: form.followUpTime || undefined,
        followUpReason: form.followUpReason || undefined,
        partsLocation: form.partsLocation || undefined,
        parts: PARTS_STATUSES.includes(form.workStatus)
          ? parts.filter(p => p.partDescription).map(p => ({
              partDescription: p.partDescription,
              orderedFrom: p.orderedFrom || undefined,
              orderDate: p.orderDate || undefined,
              expectedArrival: p.expectedArrival || undefined,
              poNumber: p.poNumber || undefined,
              qty: p.qty || 1,
              instruction: p.instruction || undefined,
              failedPartNumber: p.failedPartNumber || undefined,
              failedPartSerial: p.failedPartSerial || undefined,
            }))
          : undefined,
      };

      const res = await warrantyTicketServices.create(data);
      if (res.status === 201 || res.status === 200) {
        const ticketId = res.data?.id;
        const ticketNumber = res.data?.ticketNumber || '';
        toast.success(`Ticket ${ticketNumber} created`);

        if (PARTS_STATUSES.includes(form.workStatus) && parts.some(p => p.partDescription && !p.poNumber)) {
          toast.info(`Default PO for parts: ${ticketNumber}`);
        }

        if (ticketId && partsHaveSuppliers && selectedSuppliers.length > 0) {
          try {
            const orders = selectedSuppliers.map(s => {
              let emails = [];
              if (s.id === 'other' || s.id === 'phone') emails = [];
              else if (s.emails && Array.isArray(s.emails)) emails = s.emails;
              else if (s.email) emails = [s.email];
              return {
                supplierName: s.name,
                emails,
                emailContent: generateEmailText(s.id, ticketNumber),
                items: parts.filter(p => p.supplierId === s.id && p.partDescription.trim()).map(p => ({
                  partDescription: p.partDescription, qty: p.qty || 1, instruction: p.instruction || undefined,
                })),
              };
            }).filter(o => o.emails.length > 0);
            if (orders.length > 0) {
              const orderRes = await warrantyTicketServices.sendPartsOrder(ticketId, orders, userData?.email);
              toast.success('Order email sent to suppliers!');
              const wh = orderRes?.data?.warehouse;
              if (wh?.warning) toast.warning(`Warehouse: ${wh.warning}`);
              else if (wh?.created > 0) toast.info(`Warehouse: ${wh.created} item(s) reserved${wh.skipped ? `, ${wh.skipped} already existed` : ''}`);
            }
          } catch (emailErr) {
            console.error('Failed to send order email:', emailErr);
            toast.warning('Ticket created but email sending failed');
          }
        }

        if (ticketId && selectedPhotoIds.length > 0) {
          try {
            const r = await warrantyTicketServices.attachPhotos(ticketId, selectedPhotoIds);
            const attached = r?.data?.attached ?? selectedPhotoIds.length;
            if (attached > 0) toast.success(`Attached ${attached} photo(s)`);
          } catch (photoErr) {
            console.error('Failed to attach photos:', photoErr);
            toast.warning('Ticket created but attaching photos failed');
          }
        }

        if (onSuccess) onSuccess(res.data);
      }
    } catch (e) {
      toast.error('Error creating ticket');
      console.error(e);
    }
    setSubmitting(false);
  };

  const goNextStep = () => setCurrentStep((s) => Math.min(STEP_LABELS.length - 1, s + 1));
  const goPrevStep = () => setCurrentStep((s) => Math.max(0, s - 1));

  return {
    // role / mode
    isAdminLike, expertMode, setExpertMode, currentStep, setCurrentStep, goNextStep, goPrevStep,
    // property
    selectedProperty, propertyLoading, proposals, legacyProposals, selectedProposal, selectedLegacy,
    notAirmaxxInstallation, clientInfo, ccProjectUrl, proposalSelectValue,
    selectProperty, loadPropertyById, clearProperty, resetForm, loadSuppliers,
    handleProposalSelect, handleNotAirmaxxToggle,
    // form
    form, orderType, parts, suppliers, expandedRows, errors, submitting,
    showReview, setShowReview, emailTexts, setEmailTexts,
    softWarnings, softWarningsOpen, setSoftWarningsOpen,
    selectedPhotoIds, setSelectedPhotoIds,
    handleOrderTypeChange, handleFormChange, handleDefectiveModelChange,
    handlePartChange, handlePartItemTypeChange, handlePartSupplierChange, toggleRowExpanded, addPart, removePart,
    // derived
    showSystemInfo, selectedSuppliers, partsHaveSuppliers,
    // actions
    handleReviewOrder, handleSubmit,
  };
}
